import * as d3 from "d3";

/**
 * Shared CLO graph renderer used by both CloGraphComponent and
 * CloLiteGraphComponent. Draws the CLO working-hours curve into `#svg2`.
 *
 * The only variant between the two hosts is where the Y axis label is
 * anchored (`yLabelX`), so it is passed in via the options.
 */

export interface CloGraphRenderOptions {
  /** Line/gradient stroke colour. */
  readonly color1: string;
  /** X domain (e.g. [0, 120]). */
  readonly xRange: number[];
  /** Y domain (e.g. [0, 100]). */
  readonly yRange: number[];
  /** Read-only mode disables the circle "pointer" cursor. */
  readonly readOnly: boolean;
  /** X coordinate for the rotated y-axis label (host-specific). */
  readonly yLabelX: number;
}

export const CLO_GRAPH_MARGIN = { top: 50, right: 50, bottom: 50, left: 50 };
export const CLO_GRAPH_SIZE_PX = 500;

/**
 * Render the CLO working-hours curve for `data` inside the `#svg2` element.
 * Returns the computed width/height so callers can reuse them if they
 * later need to add drag handlers, etc.
 */
export function renderCloGraph(
  data: LegacyAny[],
  opts: CloGraphRenderOptions
): { svg2: LegacyAny; width: number; height: number; x: LegacyAny; y: LegacyAny; focus: LegacyAny } {
  const svg2 = d3.select("#svg2");
  const width = CLO_GRAPH_SIZE_PX - CLO_GRAPH_MARGIN.left - CLO_GRAPH_MARGIN.right;
  const height = CLO_GRAPH_SIZE_PX - CLO_GRAPH_MARGIN.top - CLO_GRAPH_MARGIN.bottom;

  const x = d3.scaleLinear().range([0, width]);
  const y = d3.scaleLinear().range([height, 0]);
  x.domain(opts.xRange);
  y.domain(opts.yRange);

  d3.line()
    .x((d: LegacyAny) => x(d.hour))
    .y((d: LegacyAny) => y(d.power));

  d3.selectAll("#svg2 > *").remove();

  const area = d3
    .area()
    .x((d: LegacyAny) => x(d.hour))
    .y0(height)
    .y1((d: LegacyAny) => y(d.power));

  const focus = svg2
    .append("g")
    .attr("transform", `translate(${CLO_GRAPH_MARGIN.left},${CLO_GRAPH_MARGIN.top})`);

  // Area
  focus
    .append("path")
    .datum(data)
    .attr("fill", "#1ECBD0")
    .attr("fill-opacity", 0.3)
    .attr("stroke", "#00b36b")
    .attr("stroke-width", "1px")
    .attr("fill", "url('#gradient1')")
    .attr("d", area);

  // Circles
  focus
    .selectAll("circle")
    .data(data)
    .enter()
    .append("circle")
    .attr("r", 5.0)
    .attr("cx", (d: LegacyAny) => x(d.hour))
    .attr("cy", (d: LegacyAny) => y(d.power))
    .style("cursor", opts.readOnly ? "default" : "pointer")
    .style("fill", "#1ECBD0");

  const maxY = d3.max(data, (d: LegacyAny) => d.power);

  // Gradient
  svg2
    .append("linearGradient")
    .attr("id", "gradient1")
    .attr("gradientUnits", "userSpaceOnUse")
    .attr("x1", 0)
    .attr("y1", y(0))
    .attr("x2", 0)
    .attr("y2", y(maxY))
    .selectAll("stop")
    .data([
      { offset: "10%", color: "transparent" },
      { offset: "30%", color: "#00E487" },
      { offset: "100%", color: opts.color1 },
    ])
    .enter()
    .append("stop")
    .attr("offset", (d: LegacyAny) => d.offset)
    .attr("stop-color", (d: LegacyAny) => d.color);

  // X axis
  focus
    .append("g")
    .attr("class", "axis axis--x")
    .attr("transform", `translate(0,${height})`)
    .call(d3.axisBottom(x));

  // X label
  svg2
    .append("text")
    .attr(
      "transform",
      `translate(${width / 2 + 40} ,${height + CLO_GRAPH_MARGIN.top + 40})`
    )
    .style("text-anchor", "middle")
    .style("font", "16px Signify-Relative-Bold")
    .html("Working hours x 1000 h");

  // Y axis
  focus
    .append("g")
    .attr("class", "axis axis--y")
    .call(d3.axisLeft(y));

  // Y label (host-specific x offset)
  svg2
    .append("text")
    .attr("transform", "rotate(-90)")
    .attr("y", 0 - CLO_GRAPH_MARGIN.left + 65)
    .attr("x", opts.yLabelX)
    .style("text-anchor", "middle")
    .style("font", "16px Signify-Relative-Bold")
    .text("Output level ( % )");

  // Grid
  focus
    .append("g")
    .attr("transform", `translate(0,${height})`)
    .call(d3.axisBottom(x).ticks(10).tickSize(-height).tickFormat(""));

  focus
    .append("g")
    .call(d3.axisLeft(y).ticks(10).tickSize(-width).tickFormat(""));

  return { svg2, width, height, x, y, focus };
}

/**
 * Shared range-validation for CLO graph data. Mirrors the identical
 * loop that used to live in both clo-graph and clo-lite-graph.
 */
export function checkCloInputRange(
  inputData: LegacyAny[],
  data: LegacyAny[],
  inputXRange: number[],
  inputYRange: number[],
  onInvalid: () => void,
  alertOnOutOfBounds: boolean
): void {
  inputData.forEach((content: LegacyAny, key: number) => {
    if (key === 0 && content["hour"] !== 0) {
      data[key]["hour"] = 0;
    }
    if (
      content["hour"] < inputXRange[0] ||
      content["hour"] > inputXRange[1]
    ) {
      onInvalid();
    }
    if (
      content["power"] < inputYRange[0] ||
      content["power"] > inputYRange[1]
    ) {
      onInvalid();
    }
    if (
      content["hour"] < 0 ||
      content["power"] < 0 ||
      content["hour"] > 120 ||
      content["power"] > 100
    ) {
      if (alertOnOutOfBounds) {
        alert("Invalid CLO values");
      }
      if (
        content["hour"] < inputXRange[0] ||
        content["hour"] > inputXRange[1]
      ) {
        data[key]["hour"] = 0;
      }
      if (content["power"] < 0 || content["power"] > 100) {
        data[key]["power"] = 100;
      }
    }
  });
}

/**
 * Convert a graph's "Non-Interpolation" step data back to its condensed
 * form (drop every second, duplicated point). Used by both CLO hosts.
 */
export function collapseNonInterpolationData(data: LegacyAny[]): LegacyAny[] {
  const collapsed: LegacyAny[] = [];
  let k = 0;
  for (let i = 0; i < data.length; i += 2) {
    collapsed[k] = data[i];
    k++;
  }
  return collapsed;
}
